<template>
    <div class="basket mb-5">
        <div v-if="basket">
            <div class="row">
                <div class="col-12 col-lg-9">
                    <div v-if="errors && errors.length > 0" class="alert alert-danger alert-dismissable">
                        <ul class="list-unstyled mb-0">
                            <li v-for="(error, itemId) in errors" :key="itemId"
                                :class="itemId !== errors.length - 1 ? 'mb-2' : ''">
                                <strong v-if="typeof basket.items[itemId] !== 'undefined'">{{
                                    basket.items[itemId].name
                                }}</strong>
                                <ul>
                                    <li v-for="(message, errorId) in error" :key="errorId">{{ message }}</li>
                                </ul>
                            </li>
                        </ul>
                    </div>

                    <div class="basket-items">
                        <div class="basket-item round drop-shadow bg-white mb-2" v-for="item in basket.items"
                            :key="item.id">
                            <div class="basket-item_info">
                                <div class="basket-item_image">
                                    <img :src="item.primary_image_url" :alt="item.name + ' Image'" width="100">
                                </div>
                                <div class="basket-item_content">
                                    <h4 class="product-name">{{ item.name }}</h4>
                                    <p class="product-options text-uppercase small">{{
                                        item.locked ? item.comment : item.options_description
                                    }}
                                    </p>
                                    <p class="small line-item-price">
                                        £{{ item.unit_price_inc_vat | money }}
                                    </p>
                                    <p v-if="item.is_used" class="small text-info">Pre-Loved</p>
                                </div>
                            </div>
                            <div class="basket-item_size">
                                <edit-basket-item-component :errors="errors" :item="item"></edit-basket-item-component>
                            </div>
                            <div class="basket-item_quantity test">
                                <div v-if="!item.locked">
                                    <a class="minus" @click="item.quantity > 1 && item.quantity--; updateBasket()"><i
                                            class="far fa-minus"></i></a>
                                    <input v-model="item.quantity" v-on:change="updateBasket()" value="1" type="text"
                                        class="px-1">
                                    <a class="plus" @click="item.quantity < max && item.quantity++; updateBasket()"><i
                                            class="far fa-plus"></i></a>
                                </div>
                                <div class="quantity-readonly" v-else>
                                    {{ item.quantity }}
                                </div>
                                <span v-if="!item.locked">
                                    <a @click="removeItem(item)" class="removeBtn" href="javascript:;">Remove</a><br>
                                </span>
                            </div>
                            <div class="basket-item_price">
                                <h4 class="mb-0">£{{ item.line_undiscounted_total | money }}</h4>
                                <span v-if="item.promotion_id">
                                    <h6 class="mb-0 text-right">&pound;{{ item.line_discount_total | money }}</h6>
                                    <hr class="m-1" />
                                    <h4 class="mb-0">&pound;{{ item.line_discounted_total | money }}</h4>
                                </span>
                                <h6 v-if="item.vat_rate > 0" class="mb-0">Inc. VAT</h6>
                            </div>
                        </div>
                    </div>

                    <div v-if="basket.total_quantity === 0" class="alert alert-info">
                        You have no items in your basket.
                    </div>
                </div>
                <div class="col-12 col-lg-3">
                    <div class="card checkout-summary bg-white round drop-shadow mt-4 mt-lg-0">
                        <div class="card-header">
                            <h6 class="mb-0">Order Summary</h6>
                        </div>
                        <!-- only show the ITEM line if there is also a discount to show -->
                        <!-- otherwise its just a duplicate line -->
                        <div class="card-body" v-if="basket.discount_total < 0 || basket.voucher_code">
                            <h4>
                                <span>Items:</span>
                                <span>&pound;{{ basket.discounted_total | money }}</span>
                            </h4>
                            <div class="mt-1">
                                <h4>
                                    <span>Discount:</span>
                                    <span>
                                      &pound;{{ basket.discount_total | money }}
                                    </span>
                                </h4>
                                <h6 v-if="basket.voucher_code" class="mt-1">{{ basket.voucher_code }}</h6>
                            </div>
                        </div>
                        <div class="card-footer">
                            <h4>
                                <span>Total:</span>
                                <span>&pound;{{ basket.grand_total | money }}</span>
                            </h4>

                            <a v-if="basket.items.length > 0" href="/checkout/global" class="btn btn-primary col-12 d-md-inline-block d-block mb-4 mb-md-0"
                                :disabled="loading">Checkout Now <i class="m-0 fas fa-arrow-right"></i></a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="loading || !basket" class="d-flex align-items-center justify-content-center mt-5">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            loading: false,
            errors: [],
        };
    },

    props: {
        basket: {
            type: Object,
        },
        max: {
            type: Number,
        },
        shoppingUrl: {
            type: String
        }
    },

    methods: {
        updateBasket() {
            this.loading = true;

            let postData = [];
            for (let item of this.basket.items) {
                item.quantity = isNaN(item.quantity) ? 1 : item.quantity;
                item.quantity = item.quantity > this.max ? this.max : item.quantity;
                item.quantity = item.quantity < 0 ? 0 : item.quantity;

                if (!item.locked) {
                    postData.push({
                        'basket_item_id': item.id,
                        'quantity': item.quantity,
                        'addon_item_comment': item.addon_item_comment
                    });
                }
            }

            axios.post('/api/v1/basket/update', postData)
                .then(res => {
                    if (res.data && res.data.success) {
                        this.$root.basket = res.data.basket;
                    }
                })
                .catch(err => {
                    if (err.response && err.response.status === 422) {
                        for (const error in err.response.data.errors) {
                            this.errors[error.split('.')[0]] = err.response.data.errors[error];
                        }

                        // Set quantity to the promotion limit if applicable
                        if(err.response.data['max_limit']) {
                            for(let item of this.basket.items) {
                                if(item.id == err.response.data['max_limit']['id']) {
                                    item.quantity = err.response.data['max_limit']['limit'];
                                }
                            }
                        }
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        removeItem(item) {
            if (confirm(`Are you sure you want to remove ${item.name} from your basket?`)) {
                const index = this.basket.items.findIndex(v => v.id === item.id);

                // Ensure the product is in the basket items.
                if (index >= 0) {
                    this.basket.items[index].quantity = 0;
                    this.updateBasket();
                }
            }
        },
    },
};
</script>
